import { countries } from '#lib/models/countryCodes.ts';
import { z } from 'zod';

export const defaultPageSize = 25;
export const defaultTimeZone = 'America/Phoenix';

export function getCountryName(countryCode: string) {
    if (!countryCode || countryCode === '') return '';

    const countryItem = countries.find((country) => country.value === countryCode);
    return countryItem?.name ?? '';
}

export const recordSchema = z.record(z.string(), z.any());
export const metaSchema = z.record(z.string(), z.record(z.string(), z.any()));

// Base schemas for list query parameters
export const pageParamsSchema = z.object({
    page: z.number().optional(),
    limit: z.number().optional()
});

export const sortablePageParamsSchema = pageParamsSchema.extend({
    sort: z.string().optional()
});

export type PageParams = z.infer<typeof pageParamsSchema>;
export type SortablePageParams = z.infer<typeof sortablePageParamsSchema>;

// Reusable field schemas
export const emailField = z.email({ message: 'Must be a valid email address' });

export type PagedResult<T> = {
    results: T[];
    meta: Record<string, unknown>;
    hasPrevious?: boolean;
    hasNext?: boolean;
    page: number;
    total: number;
    previousCursor?: string;
    nextCursor?: string;
};

export const contactNotificationSchema = z.object({
    enableNotifications: z.boolean().optional(),
    allowStatementNotifications: z.boolean().default(false),
    allowTransactionNotifications: z.boolean().default(false),
    allowMarketingNotifications: z.boolean().default(false),
    internalOnly: z.boolean().default(false),
    isPrimary: z.boolean().default(false),
    inactive: z.boolean().default(false),
    notes: z.string().optional()
});

export type ContactNotification = z.infer<typeof contactNotificationSchema>;

export const addressSchema = z.object({
    label: z.string(),
    line1: z
        .string({ message: 'Address is required' })
        .min(3, { message: 'Address must be at least 3 characters' })
        .max(100, { message: 'Address must be 100 characters or less' })
        .default(''),
    line2: z.string().optional().default(''),
    city: z
        .string({ message: 'City is required' })
        .min(2, { message: 'City must be at least 2 characters' })
        .max(50, { message: 'City must be 50 characters or less' })
        .default(''),
    stateOrProvince: z
        .string({ message: 'State or Province is required' })
        .min(2, { message: 'State or Province must be at least 2 characters' })
        .default(''),
    postalCode: z
        .string({ message: 'Postal Code is required' })
        .min(2, { message: 'Postal Code must be at least 2 characters' })
        .default(''),
    country: z.string().min(1, { message: 'Country is required' }).default('')
});

/**
 * A variant of the address schema that validates values if any are set. Otherwise, no validations occur.
 */
export const allOrNothingAddressSchema = z
    .object({
        label: z.string().default(''),
        line1: z.string(),
        line2: z.string(),
        city: z.string(),
        stateOrProvince: z.string(),
        postalCode: z.string(),
        country: z.string()
    })
    .partial()
    .superRefine((data, ctx) => {
        const hasValues = Object.values(data).some((v) => Boolean(typeof v === 'string' ? v.trim() : v));

        if (hasValues) {
            const result = addressSchema.safeParse(data);

            if (!result.success) {
                for (const issue of result.error.issues) {
                    ctx.addIssue(issue as any);
                }
            }
        }
    });

export const phoneNumberSchema = z.object({
    label: z.string(),
    number: z.string(),
    country: z.string()
});

export type Address = z.infer<typeof addressSchema>;
export type PhoneNumber = z.infer<typeof phoneNumberSchema>;
export type Meta = z.infer<typeof metaSchema>;

export const validateResultSchema = z.object({
    isValid: z.boolean(),
    message: z.string().optional()
});
export type ValidateResult = z.infer<typeof validateResultSchema>;

export interface LinkItem {
    label: string;
    parent?: string | null;
    href?: string | null;
    icon?: any | null;
    active: boolean;
    auth?: (...args: any[]) => boolean | null;
    /** Highlight only on an exact path match — for a root href that prefixes every sibling. */
    exact?: boolean;
    /** Extra paths that highlight this link — a section its href only points into, or a page that belongs under it. */
    matchHref?: string | string[];
}

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Reflects a saved contact in a local list — replacing or appending it — and, when it became primary, demotes the previous primary the server just cleared. */
export function mergeSavedContact<T extends { id: number; isPrimary: boolean }>(contacts: T[], saved: T): T[] {
    const merged = contacts.map((contact) =>
        contact.id === saved.id
            ? saved
            : saved.isPrimary && contact.isPrimary
              ? { ...contact, isPrimary: false }
              : contact
    );
    return merged.includes(saved) ? merged : [...merged, saved];
}

/** Create payloads carry isPrimary only when checked: the server elects an obligor's first contact, and an explicit false is reserved for callers that mean it. */
export function omitUncheckedPrimary<T extends { isPrimary?: boolean }>(
    payload: T
): Omit<T, 'isPrimary'> & { isPrimary?: true } {
    const { isPrimary, ...rest } = payload;
    return isPrimary ? { ...rest, isPrimary: true } : rest;
}
